import { Z_INDEX_UI } from '@/constant';
import { Box, Divider, IconButton, Stack, styled, Tooltip } from '@mui/material';
import React, { forwardRef } from 'react';

interface Props {
    width?: number;
    anchor: 'bottom' | 'top' | 'left' | 'right';
}

export const ContextualBarBox = styled(Box)<Props>(({ theme, anchor }) => {
    let anchorObject = {};
    switch (anchor) {
        case 'bottom':
            anchorObject = {
                bottom: -8,
                left: '50%',
                transform: 'translateX(-50%) translateY(100%)',
            };
            break;
        case 'top':
            anchorObject = {
                top: -40,
                left: '50%',
                transform: 'translateX(-50%) translateY(-100%)',
            };
            break;
        case 'left':
            anchorObject = {
                top: 0,
                left: -20,
                transform: 'translateX(-100%) translateY(0%)',
            };
            break;
        case 'right':
            anchorObject = {
                top: 0,
                right: -20,
                transform: 'translateX(100%) translateY(0%)',
            };
            break;
        default:
            anchorObject = {
                transform: 'translateX(-50%) translateY(100%)',
            };
            break;
    }

    return [
        {
            zIndex: Z_INDEX_UI + 1, // higher than ReiszeBorder
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            whiteSpace: 'nowrap',
            position: 'absolute',
            backgroundColor: (theme.vars || theme).palette.grey[50],
            boxShadow: (theme.vars || theme).palette.baseShadow,
            borderRadius: 4,
            transition: 'all 0.3s ease-in-out',
            overflow: 'hidden',
            padding: theme.spacing(1),
            ...anchorObject,
        },
        theme.applyStyles('dark', {
            backgroundColor: (theme.vars || theme).palette.grey[800],
        }),
    ];
});

type IconButtonProps = {
    icon: React.ReactNode;
    tooltip: string;
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
};

type ContextualBarProps = {
    anchor?: 'bottom' | 'top' | 'left' | 'right';
    startButtons: IconButtonProps[];
    midButtons?: IconButtonProps[];
    endButtons?: IconButtonProps[];
};

export const ContextualBar = forwardRef<HTMLDivElement, ContextualBarProps>(({ startButtons, midButtons, endButtons, anchor = 'bottom' }, ref) => {
    const isVertical = anchor === 'left' || anchor === 'right';

    return (
        <ContextualBarBox ref={ref} anchor={anchor}>
            <Stack spacing={1} direction={isVertical ? 'column' : 'row'} alignItems='center'>
                {startButtons.map((button, index) => (
                    <Tooltip key={`start-button-tooltip-${index}`} title={button.tooltip} placement='top'>
                        <IconButton key={`start-button-icon-${index}`} onClick={button.onClick} aria-label={`start-button-${index}-${button.tooltip}`} size='small'>
                            {button.icon}
                        </IconButton>
                    </Tooltip>
                ))}
                {midButtons && <Divider orientation={isVertical ? 'horizontal' : 'vertical'} />}
                {midButtons?.map((button, index) => (
                    <Tooltip key={`mid-button-tooltip-${index}`} title={button.tooltip} placement='top'>
                        <IconButton key={`mid-button-icon-${index}`} onClick={button.onClick} aria-label={`mid-button-${index}-${button.tooltip}`} size='small'>
                            {button.icon}
                        </IconButton>
                    </Tooltip>
                ))}
                {endButtons && <Divider orientation={isVertical ? 'horizontal' : 'vertical'} />}
                {endButtons?.map((button, index) => (
                    <Tooltip key={`end-button-tooltip-${index}`} title={button.tooltip} placement='top'>
                        <IconButton key={`end-button-icon-${index}`} onClick={button.onClick} aria-label={`end-button-${index}-${button.tooltip}`} size='small'>
                            {button.icon}
                        </IconButton>
                    </Tooltip>
                ))}
            </Stack>
        </ContextualBarBox>
    );
});
